import Select from "@common/components/Select";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  TextFieldProps,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import MenuItem from "@mui/material/MenuItem";

import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { ElogMeetingContext } from "../context";
import Typography from "@common/components/Typography";

import RadioGroupRating from "@common/components/RadioGroupRating";
import { ElogDetail } from "@services/meetings/types";

const ProducerFeedback = () => {
  const { t } = useTranslation();
  const { form, producers, dispatchElogMeetingDetails, dispatchElogMeeting } =
    useContext(ElogMeetingContext);

  enum ProducerAttendanceItem {
    NoAttendance = "Producer didn't attend the meeting",
    ConnectionOnly = "Producer monitored connections only",
    PartTime = "Producer attended",
    FullTime = "Producer attended the full meeting",
  }

  const [producerAttendance, setProducerAttendance] =
    useState<ProducerAttendanceItem>(
      form.details?.producerAttendedFullMeeting
        ? ProducerAttendanceItem.FullTime
        : form.details?.producerMonitoredConnections
        ? ProducerAttendanceItem.ConnectionOnly
        : (form.details?.producerAttendanceMinutes ?? 0) > 0
        ? ProducerAttendanceItem.PartTime
        : ProducerAttendanceItem.NoAttendance
    );

  const handleProducerAttendance = (attendance: ProducerAttendanceItem) => {
    setProducerAttendance(attendance);
    const elog: ElogDetail = {
      producerAttendedFullMeeting: undefined,
      producerMonitoredConnections: undefined,
      producerAttendanceMinutes: undefined,
    };
    switch (attendance) {
      case ProducerAttendanceItem.NoAttendance:
        break;
      case ProducerAttendanceItem.PartTime:
        elog.producerAttendanceMinutes = 0;
        break;
      case ProducerAttendanceItem.ConnectionOnly:
        elog.producerMonitoredConnections = true;
        break;
      case ProducerAttendanceItem.FullTime:
        elog.producerAttendedFullMeeting = true;
        break;
    }
    dispatchElogMeetingDetails(elog);
  };
  const inputProps: TextFieldProps = {
    size: "small",
    InputProps: {
      endAdornment: (
        <InputAdornment position="end">{t("(mins)")}</InputAdornment>
      ),
    },
    sx: { margin: 1, width: "24rem" },
  };

  const onChangeAttendanceMinutes = (value: number) => {
    dispatchElogMeetingDetails({
      producerAttendanceMinutes: value,
    });
  };

  const resetProducerFeedback = () => {
    dispatchElogMeetingDetails({
      produced: false,
      producerAttendedFullMeeting: undefined,
      producerMonitoredConnections: undefined,
      producerAttendanceMinutes: undefined,
      producer: undefined,
      succeeded: false,
      qualityAssessment: undefined,
    });
    dispatchElogMeeting({ note: "" });
  };

  return (
    <Grid
      container
      direction="column"
      sx={{ width: '100%', gap: "1rem",
        padding: "1rem", }}
    >
      {form.elogMeeting === undefined ? (
        <CircularProgress sx={{ position: "relative", left: "50%" }} />
      ) : (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.details?.produced ?? false}
                onChange={(_, checked) =>
                  checked
                    ? dispatchElogMeetingDetails({ produced: checked })
                    : resetProducerFeedback()
                }
              />
            }
            label={
              <Typography size="small">
                {t("This meeting was produced")}
              </Typography>
            }
            key="elogProduced"
          />
          <Box sx={{ marginLeft: "2rem" }}>
            <FormControlLabel
              sx={{ paddingBottom: "1rem" }}
              control={
                <Checkbox
                  checked={form.details?.succeeded ?? false}
                  onChange={(_, checked) =>
                    dispatchElogMeetingDetails({ succeeded: checked })
                  }
                  disabled={!(form.details?.produced ?? false)}
                />
              }
              label={
                <Typography size="small">
                  {t("This meeting was successful")}
                </Typography>
              }
              key="elogProduced"
            />
            <Select
              label={t("Producer Name")}
              value={form.details?.producer?.id ?? ""}
              onChange={(event) => {
                let producer = producers.find(
                  (producer) => producer.id == event.target.value
                );
                dispatchElogMeetingDetails({
                  producer: producer,
                });
              }}
              displayEmpty
              disabled={!(form.details?.produced ?? false)}
            >
              <MenuItem key="producerempty" value=""></MenuItem>
              {producers.map((producer) => (
                <MenuItem key={producer.id} value={producer.id}>
                  {`${producer.firstName} ${producer.lastName}`}
                </MenuItem>
              ))}
            </Select>
            <FormControl
              fullWidth
              sx={(theme) => ({
                marginTop: 2,
                width: "100%",
              })}
            >
              <Typography>{t("Producer Attendance")}</Typography>
              <RadioGroup
                value={producerAttendance}
                onChange={(e) =>
                  handleProducerAttendance(
                    e.target.value as ProducerAttendanceItem
                  )
                }
                name="radio-attendance-group"
                sx={{ marginLeft: "1rem" }}
                key="radio-attendance-group"
              >
                {Object.values(ProducerAttendanceItem).map(
                  (attendance, index) =>
                    attendance != ProducerAttendanceItem.PartTime ? (
                      <FormControlLabel
                        value={attendance}
                        control={
                          <Radio key={`radio-attendance-key-${index}`} />
                        }
                        label={t(attendance)}
                        key={`formcontrol-label-attendance-${index}`}
                        disabled={!(form.details?.produced ?? false)}
                      />
                    ) : (
                      <div key={`formcontrol-div-attendance-${index}`}>
                        <FormControlLabel
                          value={attendance}
                          control={
                            <Radio key={`radio-attendance-key-${index}`} />
                          }
                          label={t(attendance)}
                          key={`formcontrol-label-attendance-${index}`}
                          disabled={!(form.details?.produced ?? false)}
                        />
                        <TextField
                          {...inputProps}
                          required
                          label={t("Attendance duration")}
                          value={
                            form.details?.producerAttendanceMinutes !==
                            undefined
                              ? form.details?.producerAttendanceMinutes
                              : ""
                          }
                          onChange={(e) => {
                            const parsed = parseInt(e.target.value);
                            onChangeAttendanceMinutes(
                              isNaN(parsed) || parsed < 0 ? 0 : parsed
                            );
                          }}
                          disabled={
                            !(form.details?.produced ?? false) ||
                            producerAttendance !=
                              ProducerAttendanceItem.PartTime
                          }
                          key="formcontrol-label-attendance-textfield"
                        />
                      </div>
                    )
                )}
              </RadioGroup>
            </FormControl>
            <Grid container direction="row" sx={{ width: '100%', paddingBottom: "1rem" }}>
              <Typography sx={{ marginRight: "3rem" }}>
                {t("Quality Assessment")}
              </Typography>
              <RadioGroupRating
                value={(form.details?.qualityAssessment ?? 0) / 2}
                setValue={(value: number | undefined) => {
                  dispatchElogMeetingDetails({
                    qualityAssessment: (value ?? 0) * 2,
                  });
                }}
                disabled={!(form.details?.produced ?? false)}
              />
            </Grid>
            <TextField
              fullWidth
              rows={4}
              id="elognote_multiline_textfield"
              label={t("Producer notes")}
              multiline
              value={form.elogMeeting.note}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                dispatchElogMeeting({ note: e.target.value })
              }
              helperText={`${form.elogMeeting?.note?.length ?? 0}/255`}
              disabled={!(form.details?.produced ?? false)}
              slotProps={{
                htmlInput: { maxLength: 255 }
              }}
            />
          </Box>
        </>
      )}
    </Grid>
  );
};

export default ProducerFeedback;
